import { useState } from 'react';
import { motion } from 'framer-motion';
import { useSkin } from '@/components/SkinContext';
import { useAdminContent } from '@/components/AdminPanel';
import { Layout } from '@/components/Layout';
import {
  BELIEFS, PROCESS_STEPS, CASES, TESTIMONIALS,
  MARKETING_CARDS, CLIENT_LOGOS,
} from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { FaWhatsapp } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function Home() {
  const { activeSkin } = useSkin();
  const siteContent = useAdminContent();

  // 🔒 LOCKED 2026-05-02: s1=White, s2=Cyan, s4=Yellow are light skins; s3=Purple, s5=Orange are dark skins
  const isLightSkin = ['s1', 's2', 's4'].includes(activeSkin.id);
  const bg = activeSkin.bg;
  const ink = activeSkin.ink;
  const subtleBg = activeSkin.subtleBg;
  const cardBg = activeSkin.card;

  // 🔒 LOCKED 2026-05-02 — Smart contrast accents per skin
  // Rule: accent must never match or blend into the skin's bg color
  // ctaBg: orange everywhere except s5 (orange bg) → yellow on s5
  const ctaBg = activeSkin.id === 's5' ? '#fcdd7c' : '#e35f38';
  const ctaText = activeSkin.id === 's5' ? '#524696' : '#ffffff';
  // accentCool: cyan everywhere except s2 (cyan bg) → orange on s2
  const accentCool = activeSkin.id === 's2' ? '#e35f38' : '#39fffa';
  // accentNumber: yellow everywhere except s4 (yellow bg) → cyan on s4
  const accentNumber = activeSkin.id === 's4' ? '#39fffa' : '#fcdd7c';

  const [form, setForm] = useState({ nome: '', email: '', mensagem: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const wa = (siteContent.contato.whatsapp || '5538991234567').replace(/\D/g, '');
    const texto = encodeURIComponent(
      `Olá Marina! Me chamo ${form.nome} (${form.email}). ${form.mensagem}`
    );
    window.open(`https://wa.me/${wa}?text=${texto}`, '_blank');
  };

  return (
    <Layout>

      {/* ══════════════════════════════════════════════
          SEÇÃO 1 — HOME / HERO
      ══════════════════════════════════════════════ */}
      <section id="home" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', backgroundColor: bg }}>
        {/* Hero background image */}
        <img
          src={IMAGES.MARINA_HERO}
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.28, pointerEvents: 'none' }}
        />
        {/* Gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to bottom, ${bg}55 0%, transparent 40%, ${bg}cc 100%)` }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 1100, margin: '0 auto', padding: 'clamp(4rem, 10vw, 6rem) clamp(1rem, 5vw, 2rem)' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} style={{ color: ink, opacity: 0.6, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '1.25rem', fontFamily: 'JetBrains Mono, monospace' }}>
              Comunicação Estratégica
            </motion.p>
            <motion.h1 variants={fadeUp} style={{ fontSize: 'clamp(2.2rem, 5.5vw, 4rem)', fontWeight: 900, color: ink, lineHeight: 1.12, marginBottom: '1.5rem', maxWidth: 800 }}>
              {siteContent.hero.headline}
            </motion.h1>
            <motion.p variants={fadeUp} style={{ color: ink, opacity: 0.8, fontSize: 'clamp(1rem, 1.8vw, 1.2rem)', lineHeight: 1.75, maxWidth: 560, marginBottom: '3rem' }}>
              {siteContent.hero.subheadline}
            </motion.p>

            {/* Stats */}
            <motion.div variants={fadeUp} style={{ display: 'flex', gap: 'clamp(1.5rem, 5vw, 3rem)', marginBottom: '3rem', flexWrap: 'wrap' }}>
              {[
                { val: siteContent.hero.stat1value, label: siteContent.hero.stat1label },
                { val: siteContent.hero.stat2value, label: siteContent.hero.stat2label },
              ].map((s) => (
                <div key={s.label}>
                  <div style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 900, color: ink, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1 }}>
                    {s.val}
                  </div>
                  <div style={{ color: ink, opacity: 0.6, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', marginTop: '0.3rem' }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* CTA */}
            <motion.a
              variants={fadeUp}
              href="#contato"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                backgroundColor: ctaBg, color: ctaText,
                padding: '0.85rem 2rem', borderRadius: '2px',
                fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none',
                letterSpacing: '0.04em',
              }}
              whileHover={{ scale: 1.03 }}
            >
              {siteContent.hero.ctaText}
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SEÇÃO 2 — MANIFESTO
      ══════════════════════════════════════════════ */}
      <section id="manifesto" style={{ backgroundColor: subtleBg, padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1rem, 5vw, 2rem)' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} style={{ color: ink, opacity: 0.55, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.75rem' }}>
              {siteContent.manifesto.subtitle}
            </motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', fontWeight: 900, color: ink, marginBottom: '1.5rem', lineHeight: 1.2 }}>
              {siteContent.manifesto.title}
            </motion.h2>
            <motion.p variants={fadeUp} style={{ color: ink, opacity: 0.85, lineHeight: 1.85, fontSize: '1.05rem', marginBottom: '2.5rem' }}>
              {siteContent.manifesto.body}
            </motion.p>

            {/* Beliefs */}
            <motion.ul variants={stagger} style={{ listStyle: 'none', padding: 0, margin: '0 0 2.5rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {BELIEFS.map((b) => (
                <motion.li key={b} variants={fadeUp} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', color: ink, fontSize: '1rem', lineHeight: 1.6 }}>
                  <span style={{ color: accentCool, fontWeight: 900, fontSize: '1.2rem', lineHeight: 1, marginTop: '0.1rem', flexShrink: 0 }}>—</span>
                  {b}
                </motion.li>
              ))}
            </motion.ul>

            <motion.blockquote
              variants={fadeUp}
              style={{
                borderLeft: `3px solid ${accentCool}`,
                paddingLeft: '1.5rem', margin: 0,
                fontStyle: 'italic', color: ink, opacity: 0.8,
                fontSize: '1.1rem', lineHeight: 1.7,
              }}
            >
              &ldquo;{siteContent.manifesto.quote}&rdquo;
            </motion.blockquote>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SEÇÃO 3 — COMO TRABALHAMOS
      ══════════════════════════════════════════════ */}
      <section id="como-trabalhamos" style={{ backgroundColor: bg, padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1rem, 5vw, 2rem)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} style={{ fontSize: '0.8rem', color: ink, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.75rem' }}>
              Processo
            </motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', fontWeight: 900, color: ink, marginBottom: '2.5rem' }}>
              Como estruturamos comunicação
            </motion.h2>

            {/* Responsive grid: 1col mobile, 2col tablet, 5col desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {PROCESS_STEPS.map((step, i) => (
                <motion.div
                  key={step.id}
                  variants={fadeUp}
                  style={{ backgroundColor: cardBg, padding: '1.5rem 1.25rem', borderRadius: '3px' }}
                >
                  <div style={{
                    fontSize: '2rem', fontWeight: 900, color: accentNumber, opacity: 1,
                    fontFamily: 'JetBrains Mono, monospace', marginBottom: '0.75rem',
                  }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 style={{ fontWeight: 700, color: ink, fontSize: '0.9rem', marginBottom: '0.5rem', lineHeight: 1.4 }}>
                    {step.title}
                  </h3>
                  <p style={{ color: ink, opacity: 0.9, fontSize: '0.82rem', lineHeight: 1.55, margin: 0 }}>
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SEÇÃO 4 — CASES
      ══════════════════════════════════════════════ */}
      <section id="cases" style={{ backgroundColor: subtleBg, padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1rem, 5vw, 2rem)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} style={{ fontSize: '0.8rem', color: ink, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.75rem' }}>
              Portfólio
            </motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', fontWeight: 900, color: ink, marginBottom: '2rem' }}>
              Cases
            </motion.h2>

            {/* 1 case destaque grande */}
            <motion.div
              variants={fadeUp}
              whileHover={{ scale: 1.005, transition: { duration: 0.2 } }}
              style={{
                position: 'relative', height: 'clamp(240px, 55vw, 420px)', borderRadius: '4px',
                overflow: 'hidden', cursor: 'pointer', marginBottom: '1rem',
              }}
            >
              <img
                src={CASES[0].image}
                alt={CASES[0].title}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.12) 55%, transparent 100%)' }} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, padding: '2rem' }}>
                <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.35rem' }}>
                  {CASES[0].subtitle}
                </p>
                <h3 style={{ color: '#fff', fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)', fontWeight: 900, margin: '0 0 0.5rem' }}>
                  {CASES[0].title}
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', margin: '0 0 1rem', maxWidth: 500 }}>
                  {CASES[0].description}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {CASES[0].tags.map((tag) => (
                    <span key={tag} style={{
                      backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff',
                      padding: '0.2rem 0.6rem', borderRadius: '2px', fontSize: '0.7rem',
                      letterSpacing: '0.08em',
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* 4 cases menores */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {CASES.slice(1, 5).map((c) => (
                <motion.div
                  key={c.id}
                  variants={fadeUp}
                  whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  style={{
                    position: 'relative', height: 'clamp(160px, 40vw, 210px)',
                    borderRadius: '4px', overflow: 'hidden', cursor: 'pointer',
                  }}
                >
                  <img
                    src={c.image}
                    alt={c.title}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
                  />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.1) 65%, transparent 100%)' }} />
                  <div style={{ position: 'absolute', bottom: 0, left: 0, padding: '1rem' }}>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.2rem' }}>
                      {c.subtitle}
                    </p>
                    <h4 style={{ color: '#fff', fontSize: '0.88rem', fontWeight: 700, margin: 0 }}>
                      {c.title}
                    </h4>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SEÇÃO 5 — TRAJETÓRIA
      ══════════════════════════════════════════════ */}
      <section id="trajetoria" style={{ backgroundColor: bg, padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1rem, 5vw, 2rem)', position: 'relative', overflow: 'hidden' }}>
        {/* 🔒 LOCKED 2026-05-02 — "MARINA" watermark text, cursive, adapts per skin */}
        <div aria-hidden="true" style={{
          position: 'absolute',
          bottom: '-0.15em',
          left: '-0.05em',
          right: 0,
          zIndex: 0,
          pointerEvents: 'none',
          fontSize: 'clamp(9rem, 20vw, 18rem)',
          fontFamily: 'Playfair Display, Georgia, serif',
          fontStyle: 'italic',
          fontWeight: 700,
          color: isLightSkin ? '#524696' : '#ffffff',
          opacity: isLightSkin ? 0.08 : 0.07,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          userSelect: 'none',
        }}>
          Marina
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-16 items-center" style={{ maxWidth: 1000, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Foto circular */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <div style={{
              width: 'clamp(180px, 50vw, 280px)', height: 'clamp(180px, 50vw, 280px)', borderRadius: '50%', overflow: 'hidden',
              border: `4px solid ${ink}`, boxShadow: `0 0 40px ${ink}33`, flexShrink: 0,
            }}>
              <img
                src={IMAGES.MARINA_PHOTO_1}
                alt="Marina Perillo"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          </motion.div>

          {/* Texto */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} style={{ fontSize: '0.8rem', color: ink, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.75rem' }}>
              Quem sou eu
            </motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 900, color: ink, marginBottom: '1.25rem' }}>
              {siteContent.trajetoria.title}
            </motion.h2>
            <motion.p variants={fadeUp} style={{ color: ink, opacity: 0.85, lineHeight: 1.85, marginBottom: '1.5rem', fontSize: '1.05rem' }}>
              {siteContent.trajetoria.bio}
            </motion.p>
            <motion.div variants={fadeUp} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {['Cultura', 'Turismo', 'Especialistas', 'Negócios Autorais', 'Agronegócio'].map((tag) => (
                <span key={tag} style={{
                  border: `1px solid ${ink}55`, color: ink, padding: '0.25rem 0.75rem',
                  borderRadius: '2px', fontSize: '0.75rem', letterSpacing: '0.08em',
                }}>
                  {tag}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SEÇÃO 6 — MARKETING CONSCIENTE
      ══════════════════════════════════════════════ */}
      <section id="marketing-consciente" style={{ backgroundColor: subtleBg, padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1rem, 5vw, 2rem)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} style={{ fontSize: '0.8rem', color: ink, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.75rem' }}>
              Reflexões
            </motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', fontWeight: 900, color: ink, marginBottom: '0.75rem' }}>
              Toda comunicação transmite uma visão de mundo.
            </motion.h2>
            <motion.p variants={fadeUp} style={{ color: ink, opacity: 0.75, lineHeight: 1.8, maxWidth: 680, marginBottom: '2.5rem', fontSize: '1rem' }}>
              Escolher como comunicar é também escolher como a marca deseja ser percebida. Quando existe clareza, a comunicação ganha consistência. E consistência constrói confiança.
            </motion.p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {MARKETING_CARDS.map((card) => (
                <motion.div
                  key={card.id} variants={fadeUp}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  style={{ backgroundColor: cardBg, padding: '1.75rem', borderRadius: '3px', cursor: 'pointer' }}
                >
                  <p style={{ color: ink, opacity: 0.5, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.6rem' }}>
                    {card.tag}
                  </p>
                  <h3 style={{ color: ink, fontWeight: 700, fontSize: '1rem', marginBottom: '0.6rem', lineHeight: 1.45 }}>
                    {card.title}
                  </h3>
                  <p style={{ color: ink, opacity: 0.75, lineHeight: 1.65, fontSize: '0.9rem', margin: 0 }}>
                    {card.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SEÇÃO 7 — CLIENTES
      ══════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════
          SEÇÃO 7 — CLIENTES
          🔒 LOCKED 2026-05-02 — logos branco filtrado, loop infinito sem gap
          Fundo: sempre escuro (s3=purple para s1, bg para outros skins escuros)
          Logos: filter brightness(0) invert(1) = branco puro, partes transparentes preservadas
          Loop: exatos 2 sets = translateX(-50%) perfeito, zero gap
      ══════════════════════════════════════════════ */}
      <section
        id="clientes"
        style={{
          backgroundColor: activeSkin.id === 's1' ? '#524696'
            : activeSkin.id === 's2' ? '#524696'
            : activeSkin.id === 's4' ? '#524696'
            : bg,
          padding: '4rem 0', overflow: 'hidden',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '1.75rem', padding: '0 2rem' }}>
          <p style={{
            color: '#39fffa',
            opacity: 0.65, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.2em',
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            Marcas que confiam
          </p>
        </div>
        {/* 🔒 Marquee: 2 sets exatos = loop sem gap. filter: brightness(0) invert(1) = branco, sem fundo */}
        <div style={{ overflow: 'hidden', position: 'relative' }}>
          <div
            className="animate-marquee"
            style={{ display: 'flex', alignItems: 'center', gap: '5rem', whiteSpace: 'nowrap' }}
          >
            {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((src, i) => (
              <img
                key={i} src={src}
                alt={`Logotipo cliente ${(i % CLIENT_LOGOS.length) + 1}`}
                style={{
                  height: 80, width: 'auto', objectFit: 'contain', flexShrink: 0,
                  filter: 'brightness(0) invert(1)',
                  opacity: 0.85,
                }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SEÇÃO 8 — DEPOIMENTOS
      ══════════════════════════════════════════════ */}
      <section id="depoimentos" style={{ backgroundColor: bg, padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1rem, 5vw, 2rem)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} style={{ fontSize: '0.8rem', color: ink, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.75rem' }}>
              O que dizem
            </motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', fontWeight: 900, color: ink, marginBottom: '2.5rem' }}>
              Depoimentos
            </motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {TESTIMONIALS.map((t) => (
                <motion.div key={t.id} variants={fadeUp} style={{ backgroundColor: cardBg, padding: '1.75rem', borderRadius: '3px' }}>
                  <div style={{ fontSize: '2.25rem', color: accentCool, opacity: 1, lineHeight: 1, marginBottom: '0.6rem' }}>"</div>
                  <p style={{ color: ink, opacity: 0.85, lineHeight: 1.7, fontSize: '0.9rem', marginBottom: '1.25rem' }}>
                    {t.text}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <img
                      src={t.photo} alt={t.name}
                      style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${ink}` }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div>
                      <p style={{ color: ink, fontWeight: 700, fontSize: '0.875rem', margin: 0 }}>{t.name}</p>
                      <p style={{ color: ink, opacity: 0.55, fontSize: '0.78rem', fontStyle: 'italic', margin: 0 }}>{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SEÇÃO 9 — CONTATO
      ══════════════════════════════════════════════ */}
      <section
        id="contato"
        style={{ backgroundColor: subtleBg, padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1rem, 5vw, 2rem)', position: 'relative', overflow: 'hidden' }}
      >
        {/* Símbolo M decorativo grande — somente desktop */}
        <div
          aria-hidden="true"
          className="hidden md:block"
          style={{
            position: 'absolute', right: '-60px', top: '50%',
            transform: 'translateY(-50%)',
            width: 480, height: 480,
            pointerEvents: 'none', zIndex: 0,
          }}
        >
          <img
            src={IMAGES.SIMBOLO_01} alt=""
            style={{
              width: '100%', height: '100%', objectFit: 'contain',
              opacity: isLightSkin ? 0.18 : 0.45,
            }}
          />
        </div>

        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} style={{ fontSize: '0.8rem', color: ink, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.75rem' }}>
              Contato
            </motion.p>
            <motion.h2 variants={fadeUp} style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', fontWeight: 900, color: ink, marginBottom: '3rem', maxWidth: 600 }}>
              {siteContent.contato.heading}
            </motion.h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" style={{ alignItems: 'start' }}>
              {/* Esquerda: cards de contato direto */}
              <motion.div variants={fadeUp} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* WhatsApp */}
                <a
                  href={`https://wa.me/${siteContent.contato.whatsapp.replace(/\D/g, '')}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '1.25rem',
                    backgroundColor: cardBg, padding: '1.5rem',
                    borderRadius: '4px', textDecoration: 'none',
                    border: `1px solid ${ink}22`, transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${ink}66`)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${ink}22`)}
                >
                  <FaWhatsapp size={32} color={ink} style={{ flexShrink: 0 }} />
                  <div>
                    <p style={{ color: ink, fontWeight: 700, fontSize: '0.95rem', margin: '0 0 0.2rem' }}>WhatsApp</p>
                    <p style={{ color: ink, opacity: 0.65, fontSize: '0.85rem', margin: 0 }}>Resposta rápida • Atendimento personalizado</p>
                  </div>
                </a>

                {/* E-mail */}
                <a
                  href={`mailto:${siteContent.contato.email}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '1.25rem',
                    backgroundColor: cardBg, padding: '1.5rem',
                    borderRadius: '4px', textDecoration: 'none',
                    border: `1px solid ${ink}22`, transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${ink}66`)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${ink}22`)}
                >
                  <MdEmail size={32} color={ink} style={{ flexShrink: 0 }} />
                  <div>
                    <p style={{ color: ink, fontWeight: 700, fontSize: '0.95rem', margin: '0 0 0.2rem' }}>E-mail</p>
                    <p style={{ color: ink, opacity: 0.65, fontSize: '0.85rem', margin: 0 }}>{siteContent.contato.email}</p>
                  </div>
                </a>
              </motion.div>

              {/* Direita: formulário */}
              <motion.form variants={fadeUp} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Input
                  placeholder="Seu nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  required
                  style={{ backgroundColor: cardBg, border: `1px solid ${ink}33`, color: ink, borderRadius: '3px' }}
                />
                <Input
                  type="email" placeholder="Seu e-mail"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  style={{ backgroundColor: cardBg, border: `1px solid ${ink}33`, color: ink, borderRadius: '3px' }}
                />
                <Textarea
                  placeholder="Como posso te ajudar?"
                  rows={4}
                  value={form.mensagem}
                  onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                  required
                  style={{ backgroundColor: cardBg, border: `1px solid ${ink}33`, color: ink, borderRadius: '3px', resize: 'none' }}
                />
                <button
                  type="submit"
                  style={{
                    backgroundColor: ctaBg, color: ctaText,
                    padding: '0.85rem', borderRadius: '3px',
                    fontWeight: 700, fontSize: '0.95rem',
                    border: 'none', cursor: 'pointer',
                    letterSpacing: '0.04em',
                  }}
                >
                  Enviar mensagem
                </button>
              </motion.form>
            </div>
          </motion.div>
        </div>
      </section>

    </Layout>
  );
}
